import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { purchaseRequestsService, offersService, dealsService, travelerTripsService } from '../../services/supabaseService';
import { realTimeMonitoringService } from '../../services/realTimeMonitoringService';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import DealStatusIndicator from '../../components/ui/DealStatusIndicator';
import Icon from '../../components/AppIcon';
import BuyerMode from './components/BuyerMode';
import TravelerMode from './components/TravelerMode';

const UnifiedDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeMode, setActiveMode] = useState('buyer');
  const [loading, setLoading] = useState(true);
  const [buyerData, setBuyerData] = useState({ requests: [], offers: [] });
  const [travelerData, setTravelerData] = useState({ requests: [], offers: [], deals: [], trips: [] });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    const savedMode = localStorage.getItem('dashboardMode') || userProfile?.role || 'buyer';
    setCurrentLanguage(savedLanguage);
    setActiveMode(savedMode);
  }, [userProfile]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
      // Start real-time monitoring for AI matching notifications
      realTimeMonitoringService?.startMonitoring();
    }
    
    return () => {
      // Cleanup monitoring on unmount
      realTimeMonitoringService?.stopMonitoring();
    };
  }, [user, activeMode]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      if (activeMode === 'buyer') {
        const [requests, offers] = await Promise.all([
          purchaseRequestsService?.getByBuyerId(user?.id),
          purchaseRequestsService?.getByBuyerId(user?.id)?.then(reqs => {
            if (reqs?.length > 0) {
              const requestIds = reqs?.map(r => r?.id);
              return offersService?.getByRequestIds(requestIds);
            }
            return [];
          })
        ]);
        setBuyerData({ requests: requests || [], offers: offers || [] });
      } else {
        const [requests, offers, deals, trips] = await Promise.all([
          purchaseRequestsService?.getActiveRequests(),
          offersService?.getByTravelerId(user?.id),
          dealsService?.getByTravelerId(user?.id),
          travelerTripsService?.getByTravelerId(user?.id)
        ]);
        setTravelerData({ 
          requests: requests || [], 
          offers: offers || [], 
          deals: deals || [],
          trips: trips || []
        });
      }
    } catch (error) {
      console.error('Load dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModeSwitch = (mode) => {
    setActiveMode(mode);
    localStorage.setItem('dashboardMode', mode);
    localStorage.setItem('userRole', mode);
  };

  const translations = {
    en: {
      buyerMode: 'Buyer Mode',
      travelerMode: 'Traveler Mode',
      switchToBuyer: 'Switch to Buyer',
      switchToTraveler: 'Switch to Traveler',
      buyerDesc: 'Create purchase requests and receive offers',
      travelerDesc: 'Browse requests and submit offers'
    },
    ko: {
      buyerMode: '구매자 모드',
      travelerMode: '여행자 모드',
      switchToBuyer: '구매자로 전환',
      switchToTraveler: '여행자로 전환',
      buyerDesc: '구매 요청 생성 및 제안 받기',
      travelerDesc: '요청 탐색 및 제안 제출'
    }
  };

  const t = translations?.[currentLanguage];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <DealStatusIndicator />
      
      <main className="pt-20 pb-20 lg:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Mode Switcher */}
          <div className="mb-6 bg-card rounded-lg shadow-elevation-md p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div>
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                    {activeMode === 'buyer' ? t?.buyerMode : t?.travelerMode}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {activeMode === 'buyer' ? t?.buyerDesc : t?.travelerDesc}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleModeSwitch(activeMode === 'buyer' ? 'traveler' : 'buyer')}
                className="flex items-center gap-2 px-4 h-10 rounded-lg bg-secondary text-secondary-foreground hover:opacity-90 transition-base"
              >
                <Icon name="RefreshCw" size={18} />
                <span className="text-sm font-medium">
                  {activeMode === 'buyer' ? t?.switchToTraveler : t?.switchToBuyer}
                </span>
              </button>
            </div>
          </div>

          {/* Mode Content */}
          {activeMode === 'buyer' ? (
            <BuyerMode 
              data={buyerData}
              onRefresh={loadDashboardData}
              currentLanguage={currentLanguage}
            />
          ) : (
            <TravelerMode 
              data={travelerData}
              onRefresh={loadDashboardData}
              currentLanguage={currentLanguage}
            />
          )}
        </div>
      </main>

      <BottomTabNavigation />
    </div>
  );
};

export default UnifiedDashboard;